import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { BulkReference, BulkReferencePlacementSummary } from '@/api/bulk';
import { formatRelativeDate } from '@/lib/format-date';
import { proxyStorageUrl } from '@/lib/product-transformer';
import { cn } from '@/lib/utils';

/**
 * "Front · Standard · 280 mm breit": position, size tier and motif width of
 * the master placement; parts the card data lacks are left out. Shared with
 * the step 03 summary.
 */
export function formatPlacementSummary(
  t: TFunction,
  placement: BulkReferencePlacementSummary | null | undefined
): string {
  if (!placement) return '';
  return [
    t(`seller.bulk.reference.card.positions.${placement.positionSlug}`),
    placement.sizeTier
      ? t(`seller.bulk.reference.card.sizeTiers.${placement.sizeTier}`)
      : null,
    placement.designWidthMm !== null
      ? t('seller.bulk.reference.card.motifWidth', {
          width: placement.designWidthMm,
        })
      : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

export interface ReferenceCardProps {
  reference: BulkReference;
  /** Shared `name` of the radio inputs in the list. */
  groupName: string;
  selected: boolean;
  onSelect: (reference: BulkReference) => void;
}

/**
 * One reference listing (R10): preview, name, method, placement with motif
 * size of the master product, relative publish date and product count. The
 * whole card is the label of a native radio, so click, keyboard and screen
 * readers all select through the same input (label content is phrasing
 * only, hence spans throughout).
 */
export function ReferenceCard({
  reference,
  groupName,
  selected,
  onSelect,
}: ReferenceCardProps) {
  const { t, i18n } = useTranslation();
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewUrl = proxyStorageUrl(reference.thumbnailUrl);

  const methodNames = reference.methods.map((method) => method.name).join(', ');
  const placementText = formatPlacementSummary(t, reference.placement);

  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3.5 rounded-card bg-paper-2 p-3.5 ring-1 ring-inset transition-[box-shadow,background-color] duration-[120ms] hover:bg-paper-3',
        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary',
        selected ? 'ring-2 ring-primary' : 'ring-line'
      )}
    >
      <input
        type="radio"
        name={groupName}
        value={reference.id}
        checked={selected}
        onChange={() => onSelect(reference)}
        aria-label={t('seller.bulk.reference.card.select', {
          name: reference.name,
        })}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={cn(
          'mt-1 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-pill ring-1 ring-inset',
          selected ? 'bg-primary ring-primary' : 'bg-paper ring-line'
        )}
      >
        {selected && (
          <span className="h-1.5 w-1.5 rounded-pill bg-primary-fg" />
        )}
      </span>

      <span className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-control bg-paper ring-1 ring-line-2">
        {previewUrl && !previewFailed ? (
          <img
            src={previewUrl}
            alt=""
            loading="lazy"
            onError={() => setPreviewFailed(true)}
            className="h-full w-full object-contain"
          />
        ) : (
          <>
            <ImageOff className="h-5 w-5 text-mute" aria-hidden="true" />
            <span className="sr-only">
              {t('seller.bulk.reference.card.noPreview')}
            </span>
          </>
        )}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span className="truncate text-[15px] font-semibold text-ink">
          {reference.name}
        </span>
        <span className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-[12.5px]">
          {methodNames && (
            <>
              <span className="text-mute">
                {t('seller.bulk.reference.card.method')}
              </span>
              <span className="truncate text-ink-2">{methodNames}</span>
            </>
          )}
          {placementText && (
            <>
              <span className="text-mute">
                {t('seller.bulk.reference.card.placement')}
              </span>
              <span className="truncate text-ink-2">{placementText}</span>
            </>
          )}
        </span>
        <span className="text-[12.5px] text-mute">
          {t('seller.bulk.reference.card.published', {
            date: formatRelativeDate(reference.publishedAt, i18n.language),
          })}
          {' · '}
          {t('seller.bulk.reference.card.products', {
            count: reference.productCount,
          })}
        </span>
      </span>
    </label>
  );
}
